import { supabase } from "../lib/supabaseClient";
import { Project, ProjectFile, ProjectContext, ProjectPlanStep, ProjectActivityTimeline, ProjectKnowledgeBase } from "../types";
import { getCachedAuthUser } from "./auth";

// ─── PROJECTS CRUD ──────────────────────────────────────────

const mapProjectFromDB = (proj: any): Project => {
  if (!proj) return proj;
  return {
    ...proj,
    status: proj.status === 'active' ? 'running' : proj.status,
    color: proj.color || '#f25f5c'
  };
};

const mapProjectToDB = (proj: any) => {
  if (!proj) return proj;
  const copy = { ...proj };
  if (copy.status === 'running') {
    copy.status = 'active';
  }
  return copy;
};

export const getProjects = async (): Promise<Project[]> => {
  const { data, error } = await supabase!
    .from("projects")
    .select("*")
    .order("updated_at", { ascending: false });

  if (error) {
    console.error("[Projects] Failed to fetch projects:", error);
    return [];
  }

  return (data || []).map(mapProjectFromDB);
};

export const getProject = async (id: string): Promise<Project | null> => {
  const { data, error } = await supabase!
    .from("projects")
    .select("*")
    .eq("id", id)
    .single();

  if (error) {
    console.error("[Projects] Failed to fetch project:", error);
    return null;
  }

  return mapProjectFromDB(data);
};

export const createProject = async (projectData: {
  name: string;
  description?: string;
  icon?: string;
  color?: string;
}): Promise<Project | null> => {
  const user = await getCachedAuthUser();
  if (!user) throw new Error("Authentication required");

  // Create project in Supabase database using new schema
  const { data: project, error } = await supabase!
    .from("projects")
    .insert({
      user_id: user.id,
      name: projectData.name,
      description: projectData.description || "",
      icon: projectData.icon || "💼",
      color: projectData.color || "#000000",
      status: "compiling",
      progress: 0,
      situation: "Initializing...",
    })
    .select()
    .single();

  if (error || !project) {
    console.error("[Projects] Failed to create project:", error);
    return null;
  }

  return mapProjectFromDB(project);
};

export const updateProject = async (
  id: string,
  updates: Partial<Pick<Project, "name" | "description" | "icon" | "color" | "status" | "progress" | "situation" | "confidence" | "next_action" | "monitoring">>
): Promise<Project | null> => {
  const { data, error } = await supabase!
    .from("projects")
    .update(mapProjectToDB(updates))
    .eq("id", id)
    .select()
    .single();

  if (error) {
    console.error("[Projects] Failed to update project:", error);
    return null;
  }

  return mapProjectFromDB(data);
};

export const deleteProject = async (id: string): Promise<void> => {
  const user = await getCachedAuthUser();
  if (!user) throw new Error("Authentication required");

  // Clean up storage files
  const { data: files } = await supabase!
    .from("project_files")
    .select("storage_path")
    .eq("project_id", id);

  if (files && files.length > 0) {
    const paths = files.map(f => f.storage_path);
    await supabase!.storage.from("project-files").remove(paths);
  }

  // Cascading deletes on the database take care of plan steps, activity, etc.
  const { error } = await supabase!
    .from("projects")
    .delete()
    .eq("id", id)
    .eq("user_id", user.id);

  if (error) {
    throw new Error(`Failed to delete project: ${error.message}`);
  }
};

// ─── PROJECT CONTEXT ────────────────────────────────────────

export const getProjectContext = async (id: string): Promise<ProjectContext | null> => {
  const [projectResult, stepsResult, activitiesResult, knowledgeResult, filesResult] = await Promise.all([
    supabase!.from("projects").select("*").eq("id", id).single(),
    supabase!.from("project_plan_steps").select("*").eq("project_id", id).order("position"),
    supabase!.from("project_activity_timeline").select("*").eq("project_id", id).order("timestamp", { ascending: false }),
    supabase!.from("project_knowledge_base").select("*").eq("project_id", id),
    supabase!.from("project_files").select("*").eq("project_id", id).is("record_id", null).order("created_at", { ascending: false }),
  ]);

  if (projectResult.error || !projectResult.data) {
    console.error("[Projects] Failed to fetch project context:", projectResult.error);
    return null;
  }

  return {
    project: projectResult.data,
    planSteps: stepsResult.data || [],
    activities: activitiesResult.data || [],
    knowledge: knowledgeResult.data || [],
    files: filesResult.data || [],
  };
};

// ─── PROJECT FILES ──────────────────────────────────────────

export const getProjectFiles = async (projectId: string): Promise<ProjectFile[]> => {
  const { data, error } = await supabase!
    .from("project_files")
    .select("*")
    .eq("project_id", projectId)
    .is("record_id", null)
    .order("created_at", { ascending: false });

  if (error) {
    console.error("[Projects] Failed to fetch files:", error);
    return [];
  }

  return data || [];
};

export const uploadProjectFile = async (
  projectId: string,
  file: File,
  recordId?: string
): Promise<ProjectFile | null> => {
  const user = await getCachedAuthUser();
  if (!user) throw new Error("Authentication required");

  const storagePath = `${user.id}/${projectId}/${Date.now()}_${file.name}`;

  const { error: uploadError } = await supabase!.storage
    .from("project-files")
    .upload(storagePath, file);

  if (uploadError) {
    console.error("[Projects] Upload failed:", uploadError);
    throw new Error(`Upload failed: ${uploadError.message}`);
  }

  const { data, error } = await supabase!
    .from("project_files")
    .insert({
      project_id: projectId,
      user_id: user.id,
      record_id: recordId || null,
      name: file.name,
      file_type: file.type || "application/octet-stream",
      size_bytes: file.size,
      storage_path: storagePath,
    })
    .select()
    .single();

  if (error) {
    console.error("[Projects] Failed to record file metadata:", error);
    await supabase!.storage.from("project-files").remove([storagePath]);
    return null;
  }

  return data;
};

export const deleteProjectFile = async (fileId: string, storagePath: string): Promise<void> => {
  await Promise.all([
    supabase!.storage.from("project-files").remove([storagePath]),
    supabase!.from("project_files").delete().eq("id", fileId),
  ]);
};
